import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { io } from 'socket.io-client';
import * as XLSX from 'xlsx';
import { useLocation } from 'react-router';

// hooks
import { useAuthContext } from 'src/auth/hooks';

// utils
import axiosInstance, { endpoints } from 'src/utils/axios';

// env
const HOST_API = import.meta.env.VITE_HOST_API;

// context
export const ChatbotContext = createContext(undefined);

export const ChatbotConsumer = ChatbotContext.Consumer;

export const ChatbotProvider = ({ children }) => {
  const socketRef = useRef(null);
  const { companyId, discoveryOrgId } = useAuthContext();
  const location = useLocation();

  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [threadId, setThreadId] = useState('');
  const [assetTypes, setAssetTypes] = useState([]);
  const [formData, setFormData] = useState({
    ticketTitle: '',
    ticketCategory: '',
    description: '',
  });
  const [messages, setMessages] = useState([
    {
      id: '1',
      type: 'assistant',
      isExcel: false,
      raiseTicket: false,
      isAssetsList: false,
      isSubmitTicket: false,
      assetList: [],
      excelData: [],
      content: 'Hello! I’m Self Heal. I’m here to assist you .',
      timestamp: new Date(),
    },
  ]);

  // -------Asset Types----------
  const handleAssetTypeRequest = useCallback(
    (thread) => {
      const assetTypeList = assetTypes?.map((item) => item?.name)?.join(', ');
      const record = { text: assetTypeList };

      if (thread) record.threadId = thread;
      if (companyId) record.companyId = companyId;

      socketRef.current?.emit('flexAI_message', JSON.stringify(record));
    },
    [assetTypes, companyId]
  );

  // ------Ticket---------
  const handleTicketResponse = useCallback((payload) => {
    setFormData({
      ticketTitle: payload.ticketTitle || '',
      ticketCategory: payload.ticketType || '',
      description: payload.notes || '',
    });

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        type: 'assistant',
        content: 'Ticket Details',
        isExcel: false,
        raiseTicket: false,
        isAssetsList: false,
        isSubmitTicket: true,
        assetList: [],
        timestamp: new Date(),
      },
    ]);
    setIsTyping(false);
  }, []);

  // --------Assets List------------
  const handleAssetsListResponse = useCallback(
    (payload) => {
      setIsTyping(false);
      const suggestedAssetType = assetTypes?.find((item) => item?.name === payload.assetType);
      console.log('suggestedAssetType', assetTypes);
      console.log('payload', payload.assetType);

      axiosInstance
        .get(endpoints.chatbot.assetsPerUser, {
          params: { assetTypeId: suggestedAssetType?._id },
        })
        .then((res) => {
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              type: 'assistant',
              content: 'Select Your Device',
              isExcel: false,
              raiseTicket: false,
              isSubmitTicket: false,
              isAssetsList: true,
              assetList: res.data || [],
              timestamp: new Date(),
            },
          ]);
        })
        .catch((err) => console.error(err.message));
    },
    [assetTypes]
  );

  const handleDefaultReply = useCallback((payload, text) => {
    const isArray = Array.isArray(text);
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        type: 'assistant',
        isExcel: isArray,
        raiseTicket: payload?.raiseTicket || false,
        isAssetsList: payload?.isAssetsList || false,
        isSubmitTicket: false,
        excelData: isArray ? text : [],
        content: isArray ? 'Report :' : payload?.message || '',
        timestamp: new Date(),
      },
    ]);
    setIsTyping(false);
  }, []);

  const handleFlexAIReply = useCallback(
    (data) => {
      console.log('AI Reply', data);

      const thread = data?.threadId;
      const payload = data?.data;
      const text = data?.text;

      if (thread) setThreadId(thread);

      if (payload?.isAssetTypeNeeded) return handleAssetTypeRequest(thread);
      if (payload?.type === 'ticket') return handleTicketResponse(payload);
      if (payload?.isAssetsList) return handleAssetsListResponse(payload);

      return handleDefaultReply(payload, text);
    },
    [handleAssetTypeRequest, handleTicketResponse, handleAssetsListResponse, handleDefaultReply]
  );

  const setupSocket = useCallback(() => {
    const token = localStorage.getItem('jwt_access_token');

    const socket = io(`${HOST_API}/chat`, {
      query: { token },
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      console.log('✅ Socket connected:', socket.id);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
      console.log('❌ Socket disconnected');
    });

    socket.on('flexAI_reply', handleFlexAIReply);
    socket.on('typing', setIsTyping);
  }, [handleFlexAIReply]);

  // useEffect(() => {
  //   setupSocket();
  // }, [setupSocket]);

  const connectSocket = useCallback(() => {
    if (socketRef.current?.connected) return;
    setupSocket();
  }, [setupSocket]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    axiosInstance
      .get(endpoints.chatbot.getAllAssetTypes)
      .then((res) => {
        setAssetTypes(res?.data);
      })
      .catch((err) => console.error(err.message));
  }, []);

  const sendMessage = useCallback(
    (text) => {
      const record = { text };

      if (threadId && threadId.trim() !== '') {
        record.threadId = threadId;
      }

      if (companyId) {
        record.companyId = companyId;
      }

      socketRef.current?.emit('flexAI_message', JSON.stringify(record));
      setIsTyping(true);
    },
    [companyId, threadId]
  );

  // Excel
  const exportToExcel = useCallback((data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Assets');
    XLSX.writeFile(workbook, 'asset_report.xlsx');
  }, []);

  // ---------------------

  const chatbotContextValue = useMemo(
    () => ({
      socket: socketRef.current,
      connectSocket,
      disconnectSocket,
      messages,
      setMessages,
      sendMessage,
      isTyping,
      isConnected,
      exportToExcel,
      formData,
      setFormData,
    }),
    [
      connectSocket,
      disconnectSocket,
      messages,
      sendMessage,
      isTyping,
      isConnected,
      exportToExcel,
      formData,
    ]
  );

  return <ChatbotContext.Provider value={chatbotContextValue}>{children}</ChatbotContext.Provider>;
};

export const useChat = () => useContext(ChatbotContext);
